import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Check, Circle, ExternalLink, Loader2, Store } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Vendor onboarding — the walk from a claimed invite to an open storefront.
 *
 * Deliberately lighter than the practitioner wizard: his call was a tick per document and one
 * signature, not a checkpoint per clause. Everything else the maker needs is already built —
 * the vendor application collects the store, shipping, returns and fees — so this page's job is
 * to record the agreement and point at the next step.
 */
export default function VendorOnboarding() {
  const [status, setStatus] = useState(null);
  const [docs, setDocs] = useState([]);
  const [signed, setSigned] = useState(null);
  const [ticked, setTicked] = useState({});
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/vendor-onboarding/status`, auth()),
        axios.get(`${API}/vendor-onboarding/agreements`, auth()),
      ]);
      setStatus(s.data);
      setDocs(a.data.documents || []);
      setSigned(a.data.signed || null);
    } catch {
      // Not an error worth shouting about: an admin or a member who simply isn't a vendor
      // lands here from a stale link, and a red toast made it look broken.
      setStatus({ show: false, steps: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allTicked = docs.length > 0 && docs.every((d) => ticked[d.document_type]);

  const sign = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/vendor-onboarding/accept-agreements`, {
        signature_name: signature.trim(),
        accepted_types: docs.filter((d) => ticked[d.document_type]).map((d) => d.document_type),
      }, auth());
      toast.success('Agreements recorded — your store details are next');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="p-10 flex justify-center" data-testid="vendor-onboarding-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!status.invited && !status.application_submitted && !status.agreements_signed) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="vendor-onboarding-not-a-vendor">
        <Store className="w-8 h-8 text-gray-300 mx-auto mb-4" />
        <h1 className="text-base md:text-lg font-semibold mb-2">Nothing to finish here</h1>
        <p className="text-sm text-gray-500">
          This page walks an invited maker through opening a storefront. Have a look around the
          marketplace to see where the storefronts live.
        </p>
        <Link to="/dashboard/marketplace">
          <Button className="mt-5" data-testid="vendor-onboarding-marketplace-btn">
            Go to the Marketplace
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="vendor-onboarding-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Store className="w-5 h-5 text-[#c4622d]" />
          <h1 className="text-lg md:text-xl font-semibold">Opening your storefront</h1>
        </div>
        <p className="text-sm text-gray-500">
          {status.next_step}
          {status.total_steps ? ` · step ${Math.min(status.step, status.total_steps)} of ${status.total_steps}` : ''}
        </p>
      </div>

      <Card data-testid="vendor-onboarding-steps">
        <CardContent className="p-0 divide-y">
          {(status.steps || []).map((s) => (
            <div key={s.key} className="flex items-center gap-3 px-5 py-4"
                 data-testid={`vendor-step-${s.key}`}>
              {s.done
                ? <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                : <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              <span className={`text-sm flex-1 ${s.done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                {s.label}
              </span>
              {!s.done && s.link && (
                <Link to={s.link}>
                  <Button size="sm" variant="outline" data-testid={`vendor-step-go-${s.key}`}>
                    Go <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {!status.agreements_signed ? (
        <Card data-testid="vendor-agreements-card">
          <CardContent className="p-6 space-y-4">
            <div>
              <h2 className="text-base md:text-lg font-semibold">Your vendor agreements</h2>
              <p className="text-sm text-gray-500 mt-1">
                Read each one, tick it, and sign once at the bottom. Open in a new tab — nothing here
                is lost.
              </p>
            </div>

            {docs.map((d) => (
              <label key={d.document_type}
                     className="flex items-start gap-3 border rounded-lg p-4 cursor-pointer
                                transition-colors duration-200 hover:bg-gray-50"
                     data-testid={`vendor-doc-${d.document_type}`}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!ticked[d.document_type]}
                  onChange={(e) => setTicked({ ...ticked, [d.document_type]: e.target.checked })}
                  data-testid={`vendor-doc-tick-${d.document_type}`}
                />
                <span className="flex-1">
                  <span className="text-sm font-medium text-gray-900 block">{d.title}</span>
                  <span className="text-xs text-gray-500">Version {d.version}</span>
                </span>
                <a href={d.route} target="_blank" rel="noreferrer"
                   className="text-xs text-[#c4622d] underline flex items-center gap-1"
                   data-testid={`vendor-doc-open-${d.document_type}`}>
                  Read <ExternalLink className="w-3 h-3" />
                </a>
              </label>
            ))}

            <div>
              <label className="text-sm text-gray-700 block mb-1" htmlFor="vendor-signature">
                Type your full legal name to sign
              </label>
              <Input
                id="vendor-signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="e.g. Jane Marie Cooper"
                data-testid="vendor-signature-input"
              />
            </div>

            <Button
              onClick={sign}
              disabled={busy || !allTicked || signature.trim().length < 2}
              className="bg-[#c4622d] hover:bg-[#a9511f]"
              data-testid="vendor-agreements-sign-btn"
            >
              {busy ? 'Recording…' : 'Agree and continue'}
            </Button>
            {!allTicked && (
              <p className="text-xs text-gray-400" data-testid="vendor-agreements-hint">
                Tick each document to continue.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="vendor-agreements-signed">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Agreements signed</p>
                <p className="text-xs text-gray-500 mt-1">
                  Signed as {signed?.signature_name || status.signature_name} on{' '}
                  {(signed?.signed_at || '').slice(0, 10)}. A copy is on file.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {status.application_submitted && !status.approved && (
        <Card data-testid="vendor-awaiting-review">
          <CardContent className="p-6">
            <p className="text-sm text-gray-700">
              <strong>{status.store_name}</strong> is with us for review. We&apos;ll email you the
              moment it&apos;s open — usually within a day or two.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
